import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { makeApiCall } from "src/api/apiRequests";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import ValidationMessage from "src/helpers/ValidationMessage";
import PATHS from "src/routes/Paths";

interface CreateNewConversationProp {
  showModal: boolean;
  setShowModal: (visible: boolean) => void;
  setModified: (visible: boolean) => void;
}
type SelectedObject = {
  value: string;
  label: string;
};

const CreateNewConversation: React.FC<CreateNewConversationProp> = ({
  showModal,
  setModified,
  setShowModal,
}) => {
    const { t } = useTranslation();
   const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);
  const [validations, setValidations] = useState<Record<string, string>>({});
  
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] =
    useState<SingleValue<{ value: string; label: string }>>(null);
  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] =
    useState<SingleValue<SelectedObject>>(null);


  const onClose = () => {
    setShowModal(false);
  };

  // Fetch roles
  const getRoleList = async (): Promise<void> => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.getRoles,
        "GET",
        API_HEADERS.authenticated
      );
      setRoles(response.roles.data);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
    }
  };

  const getUsers = async (roleId: string) => {
    if (!roleId) return;
    try {
      const requestUrl = `${API_PATHS.threadRecipients}?role_ids[]=${roleId}`;
      const response: any = await makeApiCall<ResponseType>(
        requestUrl,
        "GET",
        API_HEADERS.authenticated
      );
      setUsers(response.recipients);
    } catch (error: any) {
          FLASH_MESSAGES.errorMsg(error.response.data.error);
    }
  };

  useEffect(() => {
    getRoleList();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      getUsers(selectedRole.value);
    }
  }, [selectedRole]);

  
 const handleRoleChange = (e: SingleValue<SelectedObject>) => {
   setSelectedRole(e);
   setSelectedUsers(null); 
 };

  const createThread = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setBtnLoading(true);
    const formData = {
      recipients: selectedUsers ? [selectedUsers.value] : [],
    };
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.createNewChat,
        "POST",
        API_HEADERS.authenticated,
        formData
      );
      FLASH_MESSAGES.successMsg(response.success);
      setModified(true);
      onClose();
   const threadId = response.thread_id;
    navigate(PATHS.messagesOfThreads(threadId))
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <>
      <Modal toggle={() => onClose()} isOpen={showModal} centered size="md">
        <ModalHeader toggle={() => onClose()}>
          {t("create_new_conversation_modal_title")}
        </ModalHeader>
        <form onSubmit={(e) => createThread(e)}>
          <ModalBody>
            <p className="popup_subtitle mb-4">{t("add_chat_description")}</p>
           
              <div className="row">
                <div className="form-block col-12 col-md-6">
                  <label className="required">
                    {t("create_new_conversation_role")}
                  </label>
                  <Select
                    placeholder={t("create_new_conversation_choose_role")}
                    className="form-react-select"
                    classNamePrefix="select_input"
                    name="role"
                    onChange={handleRoleChange}
                    isClearable={true}
                    isSearchable={false}
                    options={roles.map((item) => ({
                      value: item.id,
                      label: item.name,
                    }))}
                    value={selectedRole}
                  />
                </div>

                <div className="form-block col-12 col-md-6">
                  <label className="required">
                    {t("create_new_conversation_name")}
                  </label>
                  <Select
                    name="name"
                    className={
                      "form-react-select" +
                      (validations.recipients ? " input-error" : "")
                    }
                    classNamePrefix="select_input"
                    onChange={(e: any) => setSelectedUsers(e)}
                    value={selectedUsers}
                    placeholder={t("create_new_conversation_enter_name")}
                    options={users.map((item) => ({
                      value: item.id,
                      label: `${item.first_name} ${item.last_name}`,
                    }))}
                  />
                  {validations.recipients && (
                    <ValidationMessage message={validations.recipients[0]} />
                  )}
                </div>
              </div>
          
          </ModalBody>
          <ModalFooter>
            <button
              className="button-white"
              type="button"
              onClick={() => onClose()}
            >
              {t("create_new_group_close")}
            </button>
            {btnLoading ? (
              <button className="button-blue has-icon" type="button">
                <i className="icon spinner-icon" />
                {t("add_chat_group_saving")}
              </button>
            ) : (
              <button className="button-blue has-icon" type="submit">
                <i className="icon plus-icon" />
                {t("add_chat_group_save")}
              </button>
            )}
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
};

export default CreateNewConversation;
